import type { TextInputSuffix } from '@ksyos/design-system';
import type { PaginationEventHandlersParams } from '@ksyos/design-system/dist/components/pagination';
import {
    ActionBar,
    Avatar,
    Box,
    Button,
    ButtonRow,
    Checkbox,
    DropdownFilter,
    Heading,
    Link,
    PageHeader,
    Pagination,
    Table,
    TableBody,
    TableCell,
    TableCellSelectRow,
    TableContainer,
    TableHead,
    TableHeader,
    TableHeaderSelectAll,
    TableRow,
    Text,
    TextInput,
} from '@ksyos/design-system';
import { useNavigate, useSearchParams } from '@ksyos/router';
import { useCallback, useEffect, useState } from 'react';
import type { ServiceCode } from '@common/enums';
import type { IServiceGroup, IServicesByServiceGroupForEpisodeCategory } from '@common/interfaces';
import type { IPractitionerFilters, IPractitionerRolFilter } from '@common/validation/practitioner';
import { ManagerAuthorization, PractitionerServiceRelationType, Profession, ServiceType } from '@common/enums';
import { KgbExportType } from '@common/enums/kgbExportType';
import { getDisplayInitials } from '@common/utils/getDisplayInitials';
import { getDisplayName } from '@common/utils/getDisplayName';
import { never } from '@common/utils/never';
import { tryParseInt } from '@common/utils/tryParseInt';
import { PageTemplate } from '@frontend/components/templates/PageTemplate';
import { assertHasAuthorization } from '@frontend/utils';
import type { IEmployment } from '../../interfaces/employment';
import { fetchJson } from '../../../store/fetch';
import { getSelectedLanguage } from '../../../translate';
import { BadgeWithLabel } from '../../elements/BadgeWithLabel';
import { TextWithMatchingTermInBold } from '../../elements/common';
import { Figure } from '../../elements/figure/Figure';
import { KgbExportModal } from '../../elements/KgbExportModal';
import { TableCellTextFieldWithMoreButton } from '../../elements/TableCellTextFieldWithMoreButton';

interface IPractitioner {
    created_at: Date;
    emailAddress: string;
    employments: Pick<IEmployment, 'organization' | 'profession' | 'relationNumber'>[];
    id: string;
    identifierDeclaration?: string;
    nameFamily: string;
    nameGiven: string | null;
    nameInfix: string | null;
    patientsCount: number;
    profilePictureUrl?: string;
    uid?: string;
}

enum PractitionerDropdownFilter {
    carepath = 'carepath',
    profession = 'profession',
    rol = 'rol',
    status = 'status',
}

interface IRolFilterChoice {
    displayText: string;
    relationType: PractitionerServiceRelationType;
    serviceCode?: ServiceCode;
    serviceTypes?: ServiceType[];
}

export function PractitionersList() {
    const navigate = useNavigate();
    const translate = getSelectedLanguage();
    const [searchParams] = useSearchParams();
    const pageSizes = [10, 15, 20, 25, 50, 100];
    const [items, setItems] = useState<IPractitioner[]>([]);
    const [isSearching, setIsSearching] = useState(true);
    const [sortDirection, setSortDirection] = useState<'ASC' | 'DESC'>('ASC');
    const [sortKey, setSortKey] = useState<'name' | 'uid'>('name');
    const [totalItems, setTotalItems] = useState(0);
    const [page, setPage] = useState(tryParseInt(searchParams.getOptional('page')) || 1);
    const [areAllChecked, setAreAllChecked] = useState(false);
    const [selectedItems, setSelectedItems] = useState<string[]>([]);
    const [pageSize, setPageSize] = useState(validateLimitSearchParam());
    const [showExportModal, setShowExportModal] = useState(false);
    const [nameOrUidFilter, setNameOrUidFilter] = useState<string | null>();
    const [filters, setFilters] = useState<IPractitionerFilters>({});
    const [filteredProfessions, setFilteredProfessions] = useState<string[]>([]);
    const [professionSearchTerm, setProfessionSearchTerm] = useState<string | null>(null);
    const [serviceGroupMembershipsGroupedByEpisodeCategories, setServiceGroupMembershipsGroupedByEpisodeCategories] =
        useState<IServicesByServiceGroupForEpisodeCategory[]>([]);
    const [onTextBoxFilterReset, setOnTextBoxFilterReset] = useState(false);
    const [onDropdownFilterReset, setOnDropdownFilterReset] = useState(false);

    const suffixPropState: TextInputSuffix = {
        icon: 'search',
        labelText: 'aria labelled text',
        onSuffixClick: async () => {
            await applySearchFilter();
        },
    };

    useEffect(() => {
        assertHasAuthorization(ManagerAuthorization.practitionerRead);
        filterProfessions();
        loadAvailableCarePathsWithServiceGroups();
    }, []);

    useEffect(() => {
        applySearchFilter();
    }, [pageSize, page, sortKey, sortDirection]);

    useEffect(() => {
        validatePageSearchParam();
    }, [totalItems]);

    useEffect(() => {
        const textFilters = [
            filters.uid,
            filters.name,
            filters.identifierDeclaration,
            filters.city,
            filters.relationNumber,
        ];
        const searchBoxesAreNotEmpty = textFilters.some(
            (textFilter) => textFilter !== null && textFilter !== undefined
        );
        if (onTextBoxFilterReset && !searchBoxesAreNotEmpty) {
            setPage(1);
            navigate(`/practitioners?page=${page}&limit=${pageSize}`);
            search();
            setOnTextBoxFilterReset(false);
        }
    }, [onTextBoxFilterReset]);

    useEffect(() => {
        if (onDropdownFilterReset) {
            setPage(1);
            navigate(`/practitioners?page=1&limit=${pageSize}`);
            search();
            setOnDropdownFilterReset(false);
        }
    }, [onDropdownFilterReset]);

    function validateLimitSearchParam() {
        const defaultLimit = 15;
        const param = tryParseInt(searchParams.getOptional('limit'), defaultLimit);

        return pageSizes.includes(param) ? param : defaultLimit;
    }

    function validatePageSearchParam() {
        if (totalItems === 0) {
            return;
        }
        const totalPages = Math.max(Math.ceil(totalItems / pageSize), 1);
        const param = tryParseInt(searchParams.getOptional('page'));
        if (param && param > totalPages) {
            setPage(1);
        }
    }

    const handlePaginationChange = useCallback(
        ({ page: newPage, pageSize: newPageSize }: PaginationEventHandlersParams) => {
            setPageSize(newPageSize);
            setPage(newPage);
            navigate(`/practitioners?page=${newPage}&limit=${newPageSize}`);
        },
        [setPageSize, setPage, navigate]
    );

    async function search() {
        const newPractitionersListExtraParameters = {
            includeEmployments: true,
            includeProfileThumbnail: true,
            limit: pageSize,
            sortBy: sortKey,
            sortDirection: sortDirection,
        };

        try {
            setIsSearching(true);
            const url = `/api/practitioners/list?page=${page}&limit=${pageSize}`;
            const result = await fetchJson(url, 'POST', {
                filters: filters,
                ...newPractitionersListExtraParameters,
            });
            setItems(result.json.items);
            setTotalItems(result.json.total);
        } finally {
            setIsSearching(false);
        }
    }

    function clearFilters() {
        setNameOrUidFilter(null);
        setFilters({ episodeCategoryIds: [], serviceGroupIds: [] });
        setOnDropdownFilterReset(true);
    }

    function clearDropdownFilters() {
        setFilters({
            ...filters,
            episodeCategoryIds: [],
            hasNoActiveEmployment: false,
            roles: null,
            selectedProfessions: null,
            serviceGroupIds: [],
        });
        setOnDropdownFilterReset(true);
    }

    async function applySearchFilter() {
        navigate(`/practitioners?page=${page}&limit=${pageSize}`);
        await search();
    }

    function isRowSelected(selectedId: string) {
        return !!selectedItems.find((id) => id === selectedId);
    }

    async function handleSelectRow(id: string) {
        let newSelectedItems = [...selectedItems];

        if (areAllChecked) {
            newSelectedItems = items
                .map((practitioner) => practitioner.id)
                .filter((practitionerId) => practitionerId !== id);
            setAreAllChecked(false);
            setSelectedItems(newSelectedItems);

            return;
        } else if (isRowSelected(id)) {
            const index = newSelectedItems.indexOf(id);
            newSelectedItems.splice(index, 1);
        } else {
            newSelectedItems.push(id);
        }

        setSelectedItems(newSelectedItems);
    }

    async function handleSort(key: 'name' | 'uid') {
        setSortDirection(sortDirection === 'ASC' && sortKey === key ? 'DESC' : 'ASC');
        setSortKey(key);
    }

    function renderActiveEmploymentDropdown() {
        return (
            <DropdownFilter.AccordionItem
                label={getDropdownFilterTitleAndCount(PractitionerDropdownFilter.status)}
                value={PractitionerDropdownFilter.status}
            >
                <Box
                    display="flex"
                    flexDirection="column"
                    gap={2}
                    pb={4}
                >
                    <Checkbox
                        checked={!!filters.hasNoActiveEmployment}
                        label={translate.practitioner.withoutActiveEmployment}
                        onChange={() =>
                            handleChangeSearchFilter('hasNoActiveEmployment', !filters.hasNoActiveEmployment)
                        }
                        value="active"
                    />
                </Box>
            </DropdownFilter.AccordionItem>
        );
    }

    function renderProfessionDropdown(suffixProp: TextInputSuffix) {
        return (
            <DropdownFilter.AccordionItem
                label={getDropdownFilterTitleAndCount(PractitionerDropdownFilter.profession)}
                value={PractitionerDropdownFilter.profession}
            >
                <Box
                    display="flex"
                    flexDirection="column"
                    gap={2}
                    pb={4}
                >
                    <TextInput
                        onChange={async (e) => await filterProfessions(e.target.value)}
                        onEnterKeyPress={async () => await filterProfessions(professionSearchTerm)}
                        onReset={async () => {
                            setProfessionSearchTerm('');
                            filterProfessions();
                        }}
                        placeholder={`${translate.common.profession}...`}
                        suffix={{
                            ...suffixProp,
                            onSuffixClick: async () => await filterProfessions(professionSearchTerm),
                        }}
                        value={professionSearchTerm ?? ''}
                    />
                    {filteredProfessions.map((profession) => {
                        return (
                            <Checkbox
                                checked={Boolean(filters.selectedProfessions?.find((item) => item === profession))}
                                key={profession}
                                label={translate.professionEnum[profession as keyof typeof translate.professionEnum]}
                                onChange={async (e) => {
                                    const selectedProfessions = filters.selectedProfessions
                                        ? [...filters.selectedProfessions]
                                        : [];
                                    if (e.target.checked) {
                                        selectedProfessions.push(profession as Profession);
                                    } else {
                                        const index = selectedProfessions.indexOf(profession as Profession);
                                        selectedProfessions.splice(index, 1);
                                    }
                                    await handleChangeSearchFilter('selectedProfessions', selectedProfessions);
                                }}
                            />
                        );
                    })}
                </Box>
            </DropdownFilter.AccordionItem>
        );
    }

    function renderCarepathDropdown() {
        return (
            <DropdownFilter.AccordionItem
                label={getDropdownFilterTitleAndCount(PractitionerDropdownFilter.carepath)}
                value={PractitionerDropdownFilter.carepath}
            >
                {serviceGroupMembershipsGroupedByEpisodeCategories.map(
                    (serviceGroupMembershipsGroupedByEpisodeCategory: IServicesByServiceGroupForEpisodeCategory) => {
                        const episodeCategory = serviceGroupMembershipsGroupedByEpisodeCategory.episodeCategory;
                        const serviceGroups = serviceGroupMembershipsGroupedByEpisodeCategory.serviceGroups;

                        return (
                            <Box
                                display="flex"
                                flexDirection="column"
                                gap={2}
                                key={episodeCategory.name}
                                pb={2}
                            >
                                <Checkbox
                                    checked={Boolean(filters.episodeCategoryIds?.includes(episodeCategory.id))}
                                    key={episodeCategory.name}
                                    label={translate.episodeCategoryEnum[episodeCategory.name]}
                                    onChange={async (e) => {
                                        const updatedEpisodeCategoryIds = [...(filters.episodeCategoryIds ?? [])];
                                        let updatedServiceGroupIds = [...(filters.serviceGroupIds ?? [])];
                                        if (e.target.checked) {
                                            updatedEpisodeCategoryIds.push(episodeCategory.id);
                                        } else {
                                            updatedEpisodeCategoryIds.splice(
                                                updatedEpisodeCategoryIds.indexOf(episodeCategory.id),
                                                1
                                            );
                                            updatedServiceGroupIds = updatedServiceGroupIds.filter(
                                                (serviceGroupId) =>
                                                    !serviceGroups
                                                        .map((serviceGroup) => serviceGroup.id)
                                                        .includes(serviceGroupId)
                                            );
                                        }
                                        await handleChangeSearchFilter('serviceGroupIds', updatedServiceGroupIds);
                                        await handleChangeSearchFilter('episodeCategoryIds', updatedEpisodeCategoryIds);
                                    }}
                                />
                                {serviceGroupMembershipsGroupedByEpisodeCategory.serviceGroups.length > 1 && (
                                    <Box
                                        display="flex"
                                        flexDirection="column"
                                        gap={2}
                                        pl={8}
                                    >
                                        {serviceGroups.map((serviceGroup: IServiceGroup) => {
                                            return (
                                                <Checkbox
                                                    checked={Boolean(
                                                        filters?.serviceGroupIds?.includes(serviceGroup.id)
                                                    )}
                                                    key={serviceGroup.id}
                                                    label={serviceGroup.name}
                                                    onChange={async (e) => {
                                                        const updatedServiceGroupIds = [
                                                            ...(filters.serviceGroupIds ?? []),
                                                        ];
                                                        const updatedEpisodeCategoryIds = [
                                                            ...(filters.episodeCategoryIds ?? []),
                                                        ];
                                                        if (e.target.checked) {
                                                            updatedServiceGroupIds.push(serviceGroup.id);
                                                            if (
                                                                !filters.episodeCategoryIds?.includes(
                                                                    episodeCategory.id
                                                                )
                                                            ) {
                                                                updatedEpisodeCategoryIds.push(episodeCategory.id);
                                                            }
                                                        } else {
                                                            const index = updatedServiceGroupIds.indexOf(
                                                                serviceGroup.id
                                                            );
                                                            updatedServiceGroupIds.splice(index, 1);
                                                        }
                                                        await handleChangeSearchFilter(
                                                            'episodeCategoryIds',
                                                            updatedEpisodeCategoryIds
                                                        );
                                                        await handleChangeSearchFilter(
                                                            'serviceGroupIds',
                                                            updatedServiceGroupIds
                                                        );
                                                    }}
                                                />
                                            );
                                        })}
                                    </Box>
                                )}
                            </Box>
                        );
                    }
                )}
            </DropdownFilter.AccordionItem>
        );
    }

    async function addRoleToFilter(filter: IPractitionerRolFilter | undefined, choice: IRolFilterChoice) {
        filter = filter ?? {};
        switch (choice.relationType) {
            case PractitionerServiceRelationType.consumer:
                filter.consumer = filter.consumer ?? {};
                if (choice.serviceTypes) {
                    filter.consumer.serviceTypes = (filter.consumer.serviceTypes ?? []).concat(choice.serviceTypes);
                }
                if (choice.serviceCode) {
                    filter.consumer.serviceCode = choice.serviceCode;
                }
                break;
            case PractitionerServiceRelationType.provider:
                filter.provider = filter.provider ?? { serviceTypes: [] };
                filter.provider.serviceTypes = filter.provider.serviceTypes.concat(choice.serviceTypes ?? []);
                break;
            case PractitionerServiceRelationType.supervisor:
                filter.supervisor = true;
                break;
            case PractitionerServiceRelationType.reader:
                filter.reader = true;
                break;
            default:
                break;
        }
    }

    async function removeRoleFromFilter(filter: IPractitionerRolFilter | undefined, choice: IRolFilterChoice) {
        if (!filter) {
            return;
        }

        switch (choice.relationType) {
            case PractitionerServiceRelationType.consumer:
                if (choice.serviceTypes && filter.consumer?.serviceTypes) {
                    filter.consumer.serviceTypes = filter.consumer.serviceTypes.filter(
                        (serviceType) => !choice.serviceTypes?.includes(serviceType)
                    );
                    if (!filter.consumer.serviceTypes.length) {
                        filter.consumer.serviceTypes = undefined;
                    }
                }
                if (choice.serviceCode && filter.consumer?.serviceCode) {
                    filter.consumer.serviceCode = undefined;
                }
                if (!Object.entries(filter.consumer ?? {}).length) {
                    filter.consumer = undefined;
                }
                break;
            case PractitionerServiceRelationType.provider:
                if (choice.serviceTypes && filter.provider?.serviceTypes) {
                    filter.provider.serviceTypes = filter.provider.serviceTypes.filter(
                        (serviceType) => !choice.serviceTypes?.includes(serviceType)
                    );
                    if (choice.serviceTypes.includes(ServiceType.examination)) {
                        filter.provider.isAnalist = undefined;
                        filter.provider.isBiometrician = undefined;
                        filter.provider.isGrader = undefined;
                    }
                }
                if (!filter.provider?.serviceTypes.length) {
                    filter.provider = undefined;
                }
                break;
            case PractitionerServiceRelationType.supervisor:
                filter.supervisor = undefined;
                break;
            case PractitionerServiceRelationType.reader:
                filter.reader = undefined;
                break;
            default:
                break;
        }
        if (Object.entries(filter).filter(([, value]) => !!value).length) {
            filter = undefined;
        }
    }

    function isRolChoiceSelected(rolChoice: IRolFilterChoice): boolean {
        const filter = filters?.roles;
        if (!filter) {
            return false;
        }

        let result: boolean;

        switch (rolChoice.relationType) {
            case PractitionerServiceRelationType.consumer:
                result = true;
                if (rolChoice.serviceTypes) {
                    result =
                        result &&
                        rolChoice.serviceTypes.filter((serviceType) =>
                            filter.consumer?.serviceTypes?.includes(serviceType)
                        ).length === rolChoice.serviceTypes.length;
                }
                if (rolChoice.serviceCode) {
                    result = result && rolChoice.serviceCode === filter.consumer?.serviceCode;
                }
                break;
            case PractitionerServiceRelationType.provider:
                result = true;
                if (rolChoice.serviceTypes) {
                    result =
                        result &&
                        rolChoice.serviceTypes.filter((serviceType) =>
                            filter.provider?.serviceTypes?.includes(serviceType)
                        ).length === rolChoice.serviceTypes.length;
                }
                break;
            case PractitionerServiceRelationType.supervisor:
                result = !!filter.supervisor;
                break;
            case PractitionerServiceRelationType.reader:
                result = !!filter.reader;
                break;
            default:
                result = false;
                break;
        }

        return result;
    }

    function renderRolDropdown() {
        const rolFilterChoices: IRolFilterChoice[] = [
            {
                displayText: 'Aanvrager consult',
                relationType: PractitionerServiceRelationType.consumer,
                serviceTypes: [ServiceType.consultation],
            },
            {
                displayText: 'Aanvrager onderzoek',
                relationType: PractitionerServiceRelationType.consumer,
                serviceTypes: [ServiceType.examination],
            },
            {
                displayText: 'Aanvrager verwijzing',
                relationType: PractitionerServiceRelationType.consumer,
                serviceTypes: [ServiceType.referral],
            },
            {
                displayText: 'Uitvoerder onderzoek',
                relationType: PractitionerServiceRelationType.provider,
                serviceTypes: [ServiceType.examination],
            },
            {
                displayText: 'Uitvoerder consult',
                relationType: PractitionerServiceRelationType.provider,
                serviceTypes: [ServiceType.consultation],
            },
            {
                displayText: 'Uitvoerder verwijzing',
                relationType: PractitionerServiceRelationType.provider,
                serviceTypes: [ServiceType.referral],
            },
            {
                displayText: 'Controleur onderzoek',
                relationType: PractitionerServiceRelationType.supervisor,
            },
            {
                displayText: 'Begeleider',
                relationType: PractitionerServiceRelationType.consumer,
                serviceTypes: [ServiceType.counseling],
            },
            {
                displayText: 'Inzage',
                relationType: PractitionerServiceRelationType.reader,
            },
        ];

        const abilityChoices: {
            displayText: string;
            flagName: 'isAnalist' | 'isBiometrician' | 'isGrader';
        }[] = [
            {
                displayText: 'Analist',
                flagName: 'isAnalist',
            },
            {
                displayText: 'Biometrie',
                flagName: 'isBiometrician',
            },
            {
                displayText: 'Grader',
                flagName: 'isGrader',
            },
        ];

        return (
            <DropdownFilter.AccordionItem
                label={getDropdownFilterTitleAndCount(PractitionerDropdownFilter.rol)}
                value={PractitionerDropdownFilter.rol}
            >
                {rolFilterChoices.map((rolChoice: IRolFilterChoice) => {
                    return (
                        <Box
                            display="flex"
                            flexDirection="column"
                            gap={2}
                            key={rolChoice.displayText}
                            pb={2}
                        >
                            <Checkbox
                                checked={isRolChoiceSelected(rolChoice)}
                                key={rolChoice.displayText}
                                label={rolChoice.displayText}
                                onChange={async (e) => {
                                    const updated = filters.roles ?? ({} as IPractitionerRolFilter);
                                    if (e.target.checked) {
                                        await addRoleToFilter(updated, rolChoice);
                                    } else {
                                        await removeRoleFromFilter(updated, rolChoice);
                                    }
                                    await handleChangeSearchFilter('roles', updated);
                                }}
                            />
                            {rolChoice.serviceTypes?.includes(ServiceType.examination) &&
                                rolChoice.relationType === PractitionerServiceRelationType.provider &&
                                filters.roles?.provider?.serviceTypes.includes(ServiceType.examination) && (
                                    <Box
                                        display="flex"
                                        flexDirection="column"
                                        gap={2}
                                        pl={8}
                                    >
                                        {abilityChoices.map((abilityChoice) => {
                                            return (
                                                <Checkbox
                                                    checked={!!(filters?.roles?.provider ?? {})[abilityChoice.flagName]}
                                                    key={abilityChoice.displayText}
                                                    label={abilityChoice.displayText}
                                                    onChange={async (e) => {
                                                        const updated = filters.roles as IPractitionerRolFilter;
                                                        updated.provider = updated.provider ?? {
                                                            serviceTypes: [ServiceType.examination],
                                                        };
                                                        updated.provider[abilityChoice.flagName] = e.target.checked
                                                            ? true
                                                            : undefined;
                                                        if (e.target.checked) {
                                                            if (abilityChoice.flagName === 'isAnalist') {
                                                                updated.provider.isBiometrician = undefined;
                                                                updated.provider.isGrader = undefined;
                                                            } else {
                                                                updated.provider.isAnalist = undefined;
                                                            }
                                                        }
                                                        await handleChangeSearchFilter('roles', updated);
                                                    }}
                                                />
                                            );
                                        })}
                                    </Box>
                                )}
                        </Box>
                    );
                })}
            </DropdownFilter.AccordionItem>
        );
    }

    async function filterProfessions(value?: string | null) {
        let filteredProfessionsList = Object.keys(Profession);
        if (value) {
            filteredProfessionsList = Object.keys(Profession).filter((item) => {
                return translate.professionEnum[item as keyof typeof Profession]
                    .toLowerCase()
                    .includes(value.toLowerCase());
            });
        }
        setFilteredProfessions(filteredProfessionsList);
        setProfessionSearchTerm(value ?? null);
    }

    function getDropdownFilterTitleAndCount(type: PractitionerDropdownFilter) {
        let dropdownFilterTitleAndCount = '';
        let carePathCount = 0;
        let rolesCount = 0;
        switch (type) {
            case PractitionerDropdownFilter.status:
                dropdownFilterTitleAndCount = `${translate.common.status} ${
                    filters.hasNoActiveEmployment ? '(1)' : ''
                }`;
                break;
            case PractitionerDropdownFilter.profession:
                dropdownFilterTitleAndCount = translate.common.profession;
                if (filters.selectedProfessions && filters.selectedProfessions.length !== 0) {
                    dropdownFilterTitleAndCount += ` (${filters.selectedProfessions?.length})`;
                }
                break;
            case PractitionerDropdownFilter.carepath:
                dropdownFilterTitleAndCount = translate.common.carePaths;
                if (filters.episodeCategoryIds) {
                    carePathCount += filters.episodeCategoryIds.length;
                    if (filters.serviceGroupIds) {
                        carePathCount += filters.serviceGroupIds.length;
                    }
                }
                dropdownFilterTitleAndCount += carePathCount ? ` (${carePathCount})` : '';
                break;
            case PractitionerDropdownFilter.rol:
                dropdownFilterTitleAndCount = 'Rol';
                if (filters.roles) {
                    if (filters.roles.consumer) {
                        rolesCount += filters.roles.consumer.serviceTypes?.length ?? 0;
                    }
                    if (filters.roles.provider) {
                        rolesCount += filters.roles.provider.serviceTypes?.length ?? 0;
                    }
                    if (filters.roles.supervisor) {
                        rolesCount++;
                    }
                    if (filters.roles.reader) {
                        rolesCount++;
                    }
                    dropdownFilterTitleAndCount += rolesCount ? ` (${rolesCount})` : '';
                }
                break;
            default:
                never(type);
        }

        return dropdownFilterTitleAndCount;
    }

    async function handleChangeNameOrUidFilter(nameOrUid: string | null) {
        let uid: number | null = null;
        let name: string | null = null;

        if (nameOrUid) {
            const parsedUid = tryParseInt(nameOrUid);
            if (parsedUid && parsedUid > 0) {
                uid = parsedUid;
            } else {
                name = nameOrUid;
            }
        }
        setNameOrUidFilter(nameOrUid);
        setFilters({ ...filters, name: name, uid: uid });
    }

    async function handleChangeSearchFilter<K extends keyof IPractitionerFilters>(
        name: K,
        value: IPractitionerFilters[K]
    ) {
        setFilters({ ...filters, [name]: value });
    }

    function getNumberOfAppliedDropdownFilters() {
        let numberOfAppliedDropdownFilters = 0;
        if (filters.hasNoActiveEmployment) {
            numberOfAppliedDropdownFilters++;
        }
        if (filters.selectedProfessions?.length) {
            numberOfAppliedDropdownFilters += filters.selectedProfessions.length;
        }
        if (filters.serviceGroupIds?.length) {
            numberOfAppliedDropdownFilters += filters.serviceGroupIds.length;
        }
        if (filters.episodeCategoryIds?.length) {
            numberOfAppliedDropdownFilters += filters.episodeCategoryIds.length;
        }
        if (filters.roles && filters.roles.consumer?.serviceTypes?.length) {
            numberOfAppliedDropdownFilters += filters.roles.consumer.serviceTypes.length;
        }
        if (filters.roles && filters.roles.provider?.serviceTypes?.length) {
            numberOfAppliedDropdownFilters += filters.roles.provider.serviceTypes.length;
        }
        if (filters.roles && filters.roles.supervisor) {
            numberOfAppliedDropdownFilters++;
        }
        if (filters.roles && filters.roles.reader) {
            numberOfAppliedDropdownFilters++;
        }

        return numberOfAppliedDropdownFilters;
    }

    function filterOutNulledEmployments(item: IPractitioner): string {
        const practitionerEmploymentProfession = item.employments.filter((employment) => {
            return employment.profession;
        });
        if (practitionerEmploymentProfession.length === 0) {
            return '';
        } else {
            return translate.professionEnum[
                practitionerEmploymentProfession[0].profession as keyof typeof translate.professionEnum
            ];
        }
    }

    async function loadAvailableCarePathsWithServiceGroups() {
        const result = await fetchJson('/api/serviceGroupMemberships?groupByEpisodeCategory=true', 'GET');
        const serviceGroupMembershipsGroupedByEpisodeCategoriesList = result.json
            .items as IServicesByServiceGroupForEpisodeCategory[];

        setServiceGroupMembershipsGroupedByEpisodeCategories(serviceGroupMembershipsGroupedByEpisodeCategoriesList);
    }

    return (
        <PageTemplate>
            <PageHeader title={translate.practitioner.practitioners} />
            <Box
                display="flex"
                flexDirection={['column', null, null, 'row']}
                flexGrow={[null, null, null, 1]}
                gap={2}
                mb={6}
                px={0}
                py={6}
            >
                <TextInput
                    onChange={async (e) => await handleChangeNameOrUidFilter(e.target.value ? e.target.value : null)}
                    onEnterKeyPress={async () => await applySearchFilter()}
                    onReset={async () => {
                        await handleChangeNameOrUidFilter(null);
                        setOnTextBoxFilterReset(true);
                    }}
                    placeholder={`${translate.practitioner.nameOrId}...`}
                    suffix={suffixPropState}
                    value={nameOrUidFilter ?? ''}
                    variant="outline"
                />
                <TextInput
                    onChange={async (e) =>
                        await handleChangeSearchFilter('identifierDeclaration', e.target.value ? e.target.value : null)
                    }
                    onEnterKeyPress={async () => await applySearchFilter()}
                    onReset={async () => {
                        await handleChangeSearchFilter('identifierDeclaration', null);
                        setOnTextBoxFilterReset(true);
                    }}
                    placeholder={`${translate.practitioner.identifierDeclaration}...`}
                    suffix={suffixPropState}
                    value={filters.identifierDeclaration ?? ''}
                    variant="outline"
                />
                <TextInput
                    onChange={async (e) =>
                        await handleChangeSearchFilter('city', e.target.value ? e.target.value : null)
                    }
                    onEnterKeyPress={async () => await applySearchFilter()}
                    onReset={async () => {
                        await handleChangeSearchFilter('city', null);
                        setOnTextBoxFilterReset(true);
                    }}
                    placeholder={`${translate.organization.location}...`}
                    suffix={suffixPropState}
                    value={filters.city ?? ''}
                    variant="outline"
                />
                <TextInput
                    onChange={async (e) =>
                        await handleChangeSearchFilter('relationNumber', e.target.value ? e.target.value : null)
                    }
                    onEnterKeyPress={async () => await applySearchFilter()}
                    onReset={async () => {
                        await handleChangeSearchFilter('relationNumber', null);
                        setOnTextBoxFilterReset(true);
                    }}
                    placeholder={`${translate.organization.relationNumber}...`}
                    suffix={suffixPropState}
                    value={filters.relationNumber ?? ''}
                    variant="outline"
                />
                <Box display="flex">
                    <DropdownFilter
                        count={getNumberOfAppliedDropdownFilters()}
                        label="Filters"
                        noPadding
                        onReset={clearDropdownFilters}
                        onSubmit={search}
                    >
                        <DropdownFilter.Accordion type="multiple">
                            {renderActiveEmploymentDropdown()}
                            {renderProfessionDropdown(suffixPropState)}
                            {renderCarepathDropdown()}
                            {renderRolDropdown()}
                        </DropdownFilter.Accordion>
                    </DropdownFilter>
                    <Box
                        alignSelf="center"
                        ml={4}
                    >
                        <Text
                            size="sm"
                            whiteSpace="nowrap"
                        >
                            <Link
                                color="subtle"
                                fontWeight="normal"
                                onClick={clearFilters}
                                variant="button"
                            >
                                {translate.common.deleteAll}
                            </Link>
                        </Text>
                    </Box>
                </Box>
            </Box>
            {items.length !== 0 && (
                <>
                    <TableContainer>
                        <Table spacing="medium">
                            <TableHead>
                                <TableRow>
                                    <TableHeaderSelectAll
                                        checked={selectedItems.length > 0 || areAllChecked}
                                        indeterminate={!areAllChecked}
                                        onSelect={async () => {
                                            setSelectedItems([]);
                                            setAreAllChecked(!areAllChecked);
                                        }}
                                    />
                                    <TableHeader
                                        isSortable
                                        isSortHeader={sortKey === 'uid'}
                                        onClick={async () => await handleSort('uid')}
                                        sortDirection={sortKey === 'uid' ? sortDirection : undefined}
                                    >
                                        ID
                                    </TableHeader>
                                    <TableHeader
                                        isSortable
                                        isSortHeader={sortKey === 'name'}
                                        onClick={async () => await handleSort('name')}
                                        sortDirection={sortKey === 'name' ? sortDirection : undefined}
                                    >
                                        {translate.common.name}
                                    </TableHeader>
                                    <TableHeader>{translate.common.profession}</TableHeader>
                                    <TableHeader>{translate.practitioner.identifierDeclaration}</TableHeader>
                                    <TableHeader>{translate.employment.relationNumber}</TableHeader>
                                    <TableHeader>{translate.organization.organization}</TableHeader>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {items.map((item) => (
                                    <TableRow
                                        key={item.id}
                                        onAuxClick={(e) => {
                                            e.preventDefault();
                                            e.stopPropagation();
                                            window.open(`/practitioners/${item.id}/profile`, '_blank');
                                        }}
                                        onClick={() => navigate(`/practitioners/${item.id}/profile`)}
                                    >
                                        <TableCellSelectRow
                                            isSelected={isRowSelected(item.id) || areAllChecked}
                                            onClick={(e) => e.stopPropagation()}
                                            onSelect={async () => await handleSelectRow(item.id)}
                                        />
                                        <TableCell>
                                            <Link
                                                to={`/practitioners/${item.id}/profile`}
                                                variant="link"
                                            >
                                                <TextWithMatchingTermInBold
                                                    customMatchStyling={{
                                                        color: 'var(--kds-color-foreground-secondary)',
                                                    }}
                                                    searchTerm={filters.uid?.toString()}
                                                    text={item.uid}
                                                />
                                            </Link>
                                        </TableCell>
                                        <TableCell style={{ padding: '12px 16px' }}>
                                            <Box
                                                alignItems="center"
                                                display="flex"
                                            >
                                                <Avatar
                                                    imageUrl={item.profilePictureUrl}
                                                    initials={getDisplayInitials({
                                                        nameFamily: item.nameFamily,
                                                        nameGiven: item.nameGiven,
                                                    })}
                                                    size="small"
                                                    sx={{
                                                        marginRight: '1rem',
                                                    }}
                                                />

                                                <TextWithMatchingTermInBold
                                                    searchTerm={filters.name}
                                                    text={getDisplayName(
                                                        {
                                                            nameFamily: item.nameFamily,
                                                            nameGiven: item.nameGiven,
                                                            nameInfix: item.nameInfix,
                                                        },
                                                        false,
                                                        'familyNameCommaFirstName'
                                                    )}
                                                />
                                            </Box>
                                        </TableCell>
                                        <TableCellTextFieldWithMoreButton
                                            characterLimit={25}
                                            content={filterOutNulledEmployments(item)}
                                        />

                                        <TableCell>
                                            <TextWithMatchingTermInBold
                                                searchTerm={filters.identifierDeclaration}
                                                text={item.identifierDeclaration}
                                            />
                                        </TableCell>
                                        <TableCellTextFieldWithMoreButton
                                            characterLimit={10}
                                            content={item.employments
                                                .map((employment) => employment.relationNumber ?? '-')
                                                .join(', ')}
                                            searchTerm={filters.relationNumber}
                                        />
                                        <TableCellTextFieldWithMoreButton
                                            characterLimit={40}
                                            content={item.employments
                                                .map(
                                                    (employment) =>
                                                        employment.organization?.name +
                                                        (employment.organization?.city
                                                            ? ` (${employment.organization.city})`
                                                            : '')
                                                )
                                                .join(', ')}
                                            searchTerm={filters.city}
                                        />
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                    <Pagination
                        isDisabled={isSearching}
                        onChange={handlePaginationChange}
                        page={page}
                        pageSize={pageSize}
                        pageSizes={pageSizes}
                        totalItems={totalItems}
                    />
                </>
            )}
            {items.length === 0 && !isSearching && (
                <Box
                    display="flex"
                    flexDirection="column"
                    justifyContent="center"
                >
                    <Figure
                        alt="Not found"
                        centered
                        imageUrl="../../../../../assets/images/not-found-search.svg"
                    />
                    <Heading
                        mb={6}
                        size="medium"
                        textAlign="center"
                    >
                        {translate.common.noSearchResults}
                    </Heading>
                    <ButtonRow justify="center">
                        <Button onClick={clearFilters}>{translate.common.deleteSearchTerms}</Button>
                    </ButtonRow>
                </Box>
            )}
            <ActionBar
                isOpen={selectedItems.length !== 0 || areAllChecked}
                onClose={async () => {
                    setSelectedItems([]);
                    setAreAllChecked(false);
                }}
            >
                <Box
                    alignItems="center"
                    display="flex"
                    gap={3}
                    justifyContent="space-between"
                >
                    <BadgeWithLabel
                        color="inverse"
                        label={translate.common.isSelected}
                        value={areAllChecked ? totalItems.toString() : selectedItems.length.toString()}
                    />
                    <Button
                        onClick={async () => {
                            setShowExportModal(true);
                        }}
                        variant="solid"
                    >
                        {translate.common.export}
                    </Button>
                </Box>
            </ActionBar>
            <KgbExportModal
                areAllChecked={areAllChecked}
                deselectItems={() => {
                    setSelectedItems([]);
                    setAreAllChecked(false);
                }}
                exportType={KgbExportType.practitioner}
                filters={areAllChecked ? filters : undefined}
                isOpen={showExportModal}
                onClose={() => {
                    setShowExportModal(false);
                }}
                selectedItems={areAllChecked ? undefined : selectedItems}
                title={`${translate.practitioner.exportPractitioners} (${
                    areAllChecked ? totalItems.toString() : selectedItems.length.toString()
                })`}
            />
        </PageTemplate>
    );
}
